import { useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";

import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import useAudio from "@/utils/hooks/useAudio";
import useColor from "@/utils/hooks/useColor";
import Valkyrie, { viArrowRotateRight, viCircleExclamation, viMusic, viPause, viPlay } from "@sippy-platform/valkyrie";

import { Card, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Slider } from "@mui/material";
import PropTypes from "prop-types";

function SoundFile({ file }) {
  const { background } = useColor(file.name);

  const pbx = useSelector((state) => state.domain.pbx);

  const source = useMemo(
    () =>
      `${urls.audio}${pbx.context}%2F${file?.id}.${file?.contentType}?alt=media&token=${file?.token}&ua=${file?.updatedAt}`,
    [pbx.context, file?.contentType, file?.id, file?.token, file?.updatedAt],
  );

  const { playable, duration, playPause, progress, seek, seekFinish, status } = useAudio(source);

  return (
    <Card id={source} key={source} style={{ overflow: "hidden", position: "relative" }}>
      <List>
        <ListItem
          disablePadding
          secondaryAction={
            <IconButton onClick={playPause} disabled={!playable} size="small" color="inherit">
              <Valkyrie
                icon={
                  status === "stopped"
                    ? viArrowRotateRight
                    : status === "paused"
                      ? viPlay
                      : status === "error"
                        ? viCircleExclamation
                        : viPause
                }
              />
            </IconButton>
          }
        >
          <ListItemButton component={NavLink} to={`sound/${file.id}`}>
            <ListItemAvatar>
              <Avatar text={file.name} icon={viMusic} />
            </ListItemAvatar>
            <ListItemText primary={file.name} />
          </ListItemButton>
        </ListItem>
      </List>
      <Slider
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "transparent",
          transition: "all .1s ease-in",
          height: 4,
          p: 0,
          "&:hover": {
            backgroundColor: background[100],
          },
          ".MuiSlider-rail": {
            height: 4,
            opacity: 0,
          },
          ".MuiSlider-track": {
            height: 4,
            backgroundColor: background[500],
          },
          ".MuiSlider-thumb": {
            display: "none",
          },
        }}
        value={progress}
        onChange={(e, value) => seek(value)}
        onChangeCommitted={seekFinish}
        min={0}
        max={duration}
        disabled={!playable}
      />
    </Card>
  );
}

SoundFile.propTypes = {
  file: PropTypes.object,
};

export default SoundFile;
